import type { MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';
import { Tooltip } from 'react-tooltip';

import { CountryCodeEnum } from '@/types/regionTypes';

import type { AddressFragment } from '@/graphql/saleor/api';
import { useIsMdUp } from '@/hooks/useMedia';
import { isEmptyString } from '@/lib/checkout';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import type { WithChildren } from '@/lib/types';
import * as M from '@/messages/myAccount';
import * as Ma from '@/messages/myAddresses';
import { useRegionProvider } from '@/providers/RegionProvider';

import { Box } from '../Box';
import { Checkbox } from '../Checkbox';
import { Typography } from '../Typography';
import { Visibility } from '../Visibility';
import { getPrefectureLabelByValue, showKanjiLabel } from './helper';

import * as S from './AddressSummary.styles';

export interface SummaryProps extends WithChildren {
  isActive?: boolean;
}

const Summary = ({ children, isActive }: SummaryProps) => (
  <S.Summary data-test-id='AddressSummary' isActive={isActive}>
    {children}
  </S.Summary>
);

export interface SummaryLineProps extends WithChildren {
  isBold?: boolean;
}

const SummaryLine = ({ children, isBold }: SummaryLineProps) => (
  <S.SummaryLine
    className={CONTENTSQUARE_MASKED}
    data-test-id='AddressSummarySummaryLine'
    isBold={isBold}
  >
    {children}
  </S.SummaryLine>
);

export interface SummaryEditProps {
  onClick: MouseEventHandler;
}

export const formatAddress = (
  address: Partial<AddressFragment>,
  countryCode?: string,
  languageCode?: string,
) => {
  const {
    city,
    country,
    countryArea,
    metadata,
    postalCode,
    streetAddress1,
    streetAddress2,
  } = address ?? {};
  const isJapaneseMarket = country?.code === CountryCodeEnum.JP;

  if (isJapaneseMarket) {
    const houseMansionName = getMetaValue(
      metadata ?? [],
      ACCOUNT_METADATA.houseMansionName,
    );
    const apartmentNumber = getMetaValue(
      metadata ?? [],
      ACCOUNT_METADATA.apartmentNumber,
    );

    const showKanji = showKanjiLabel(languageCode) ?? false;

    return (
      `${postalCode?.trim()}, ` +
      [
        countryCode,
        getPrefectureLabelByValue(showKanji, countryArea),
        !isEmptyString(city) ? city : streetAddress2,
        streetAddress1,
        houseMansionName,
        apartmentNumber,
      ]
        .filter(Boolean)
        .join(' ')
    );
  }

  return [
    streetAddress1,
    city,
    `${countryArea ?? ''} ${postalCode}`.trim(),
    countryCode,
  ]
    .filter(Boolean)
    .join(', ');
};

export const formatName = (address: Partial<AddressFragment>) => {
  const isJapaneseMarket = address?.country?.code === CountryCodeEnum.JP;
  return isJapaneseMarket
    ? `${address.lastName} ${address.firstName}`
    : `${address.firstName} ${address.lastName}`;
};

const SummaryEdit = ({ onClick }: SummaryEditProps) => {
  const { formatMessage } = useIntl();

  return (
    <S.SummaryEdit
      data-test-id='AddressSummarySummaryEdit'
      onClick={onClick}
      role='button'
    >
      <Visibility mdVisible>
        <Typography scale='p2'>
          {formatMessage(M.myAccountMessages.accountChangeCta)}
        </Typography>
      </Visibility>
      <Visibility mdHidden>
        <S.IconWrapper onClick={onClick}>
          <S.EditIcon />
        </S.IconWrapper>
      </Visibility>
    </S.SummaryEdit>
  );
};

interface AddressSummaryProps {
  address: AddressFragment;
  handleSetAsPrimaryBilling: (val: boolean) => void;
  handleSetAsPrimaryShipping: (val: boolean) => void;
  onEdit?: MouseEventHandler;
}

export const AddressSummary = ({
  address,
  handleSetAsPrimaryBilling,
  handleSetAsPrimaryShipping,
  onEdit,
}: AddressSummaryProps) => {
  const { formatMessage } = useIntl();
  const isMdUp = useIsMdUp();
  const {
    locale: { slug },
  } = useRegionProvider();

  return (
    <Summary isActive={address?.isDefaultShippingAddress || false}>
      <Tooltip
        className='tooltip-multiple-line'
        id='primary-address-required-tooltip'
        place={isMdUp ? 'right' : 'top'}
      />
      {onEdit && <SummaryEdit onClick={onEdit} />}
      {address?.isDefaultShippingAddress && (
        <Typography color='inputGrey' scale='p3'>
          {formatMessage(M.myAccountMessages.accountAddressDefault)}
        </Typography>
      )}
      <SummaryLine isBold={true}>{formatName(address)}</SummaryLine>
      <SummaryLine data-test-id='AddressSummaryAddressLine'>
        {formatAddress(address, address.country.code, slug)}
      </SummaryLine>
      <SummaryLine>{address?.country.code}</SummaryLine>
      <Box mt={1}>
        <SummaryLine>
          <span
            data-tooltip-content={
              address?.isDefaultShippingAddress
                ? formatMessage(M.myAccountMessages.primaryAddressRequired)
                : ''
            }
            data-tooltip-id='primary-address-required-tooltip'
          >
            <Checkbox
              checked={address?.isDefaultShippingAddress || false}
              data-test-id='AddressSummaryPrimaryShippingCheckbox'
              onChange={(event) =>
                handleSetAsPrimaryShipping(event.target.checked)
              }
            >
              {formatMessage(M.myAccountMessages.accountAddressPrimaryShipping)}
            </Checkbox>
          </span>
        </SummaryLine>
        <SummaryLine>
          <span
            data-tooltip-content={
              address?.isDefaultBillingAddress
                ? formatMessage(M.myAccountMessages.primaryAddressRequired)
                : ''
            }
            data-tooltip-id='primary-address-required-tooltip'
          >
            <Checkbox
              checked={address?.isDefaultBillingAddress || false}
              data-test-id='AddressSummaryPrimaryBillingCheckbox'
              onChange={(event) =>
                handleSetAsPrimaryBilling(event?.target.checked)
              }
            >
              {formatMessage(M.myAccountMessages.accountAddressPrimaryBilling)}
            </Checkbox>
          </span>
        </SummaryLine>
      </Box>
      {(!address.firstName || !address.lastName) && (
        <Typography color='error'>
          {formatMessage(Ma.myAddresses.nameMissing)}
        </Typography>
      )}
    </Summary>
  );
};
